import { useEffect, useState } from 'react'
import { useClient } from '../providers/supabase'
import { useAsyncAction } from '../hooks/useAsyncAction'
import { SignOutButton } from '../components/signOutButton'
import { SplashScreen } from './splashScreen'

interface MfaChallengeProps {
  onVerified: () => void
}

/**
 * Shown after email-OTP sign-in when the user has a verified TOTP factor and
 * the session is still at aal1. Verifying steps the session up to aal2.
 */
export function MfaChallenge({ onVerified }: MfaChallengeProps) {
  const client = useClient()

  const [factorId, setFactorId] = useState<string | null>(null)
  const [loadError, setLoadError] = useState(false)
  const [attempt, setAttempt] = useState(0)
  const [code, setCode] = useState('')

  useEffect(() => {
    let cancelled = false
    client.auth.mfa.listFactors().then(({ data, error }) => {
      if (cancelled) { return }
      // A missing list is not "no factor". Only a real array with nothing
      // verified may proceed — that is the account that never enrolled.
      if (error || !data || !Array.isArray(data.totp)) {
        setFactorId(null)
        setLoadError(true)
        return
      }
      const verified = data.totp.find((f) => f.status === 'verified')
      if (!verified) {
        onVerified()
        return
      }
      setLoadError(false)
      setFactorId(verified.id)
    }).catch(() => {
      if (cancelled) { return }
      setFactorId(null)
      setLoadError(true)
    })
    return () => { cancelled = true }
  }, [client, onVerified, attempt])

  const [verifyState, verify] = useAsyncAction(async () => {
    if (!factorId) { throw new Error('No factor available') }
    if (!/^\d{6}$/.test(code.trim())) {
      throw new Error('Enter the 6-digit code from your authenticator app.')
    }

    const { data: challenge, error: challengeError } =
      await client.auth.mfa.challenge({ factorId })
    if (challengeError) { throw challengeError }

    const { error: verifyError } = await client.auth.mfa.verify({
      factorId,
      challengeId: challenge.id,
      code: code.trim(),
    })
    if (verifyError) {
      throw new Error('That code did not match. Please try again.', { cause: verifyError })
    }

    onVerified()
  }, [client, factorId, code, onVerified])

  // A failed lookup stays here. Throwing used to reach the app-wide error
  // screen; an empty or unexpected payload used to call onVerified.
  if (loadError) {
    return <div className="App container sign-in">
      <div className="row mt-4 justify-content-center">
        <div className="col-12 col-lg-6">
          <h1>Two-factor verification</h1>
          <p>
            We couldn&rsquo;t check your authenticator. Your second factor is still required.
          </p>
          <button type="button" className="btn btn-primary" onClick={() => setAttempt((n) => n + 1)}>
            Try again
          </button>
          <hr />
          <p className="text-muted small">
            Lost access to your authenticator? <SignOutButton className="btn btn-link p-0 align-baseline">Sign out</SignOutButton> and contact support.
          </p>
        </div>
      </div>
    </div>
  }

  // While we look up the user's verified factor, show the splash screen rather
  // than a non-functional empty form.
  if (!factorId) {
    return <SplashScreen />
  }

  return <div className="App container sign-in">
    <div className="row mt-4 justify-content-center">
      <div className="col-12 col-lg-6">
        <h1>Two-factor verification</h1>
        <p>
          Enter the 6-digit code from your authenticator app to finish signing in.
        </p>

        <form onSubmit={(e) => {
            e.preventDefault();
            // The error is rendered inline via verifyState.error; suppress the
            // unhandled rejection from useAsyncAction's re-throw.
            verify().catch(() => {});
          }}>
          <input type="text" className="form-control"
            inputMode="numeric"
            autoComplete="one-time-code"
            autoFocus
            maxLength={6}
            placeholder="123456"
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))} />

          <button type="submit" className="btn btn-primary mt-2"
            disabled={verifyState.loading || code.length !== 6}>
            {verifyState.loading ? 'Verifying…' : 'Verify'}
          </button>

          {verifyState.error && <p className="text-danger mt-2">
            {verifyState.error.message}
          </p>}
        </form>

        <hr />
        <p className="text-muted small">
          Lost access to your authenticator? <SignOutButton className="btn btn-link p-0 align-baseline">Sign out</SignOutButton> and contact support.
        </p>
      </div>
    </div>
  </div>
}
